import * as L from "leaflet";
import { Pl3xMap } from "../Pl3xMap";
import { ControlBox } from "./ControlBox";
import { pixelsToMeters } from "../util/Util";
import Pl3xMapLeafletMap from "../map/Pl3xMapLeafletMap";

interface ChunkCoordinates {
    x: number;
    z: number;
}

interface ChunkSelectionMessage {
    type: "pl3xmap:chunk-selection";
    data: {
        chunks: ChunkCoordinates[];
        count: number;
        price: number;
        worldName: string;
    };
}

export class ChunkGridControl extends ControlBox {
    private _gridLayer?: L.LayerGroup;
    private _selectionLayer?: L.LayerGroup;
    private _availableLayer?: L.LayerGroup;
    private _ownedLayer?: L.LayerGroup; // Layer for already purchased chunks (white)
    private _marginLayer?: L.LayerGroup; // Layer for the buffer zone around other regions (orange)
    private _coloredLayer?: L.LayerGroup; // Persistent per-chunk colour overlay (always on)
    private _regionBorderLayer?: L.LayerGroup; // Outer border polyline per region (view mode only)
    private _highlightLayer?: L.LayerGroup; // Highlighted markup (zone) chunks
    private _isGridActive: boolean = false;
    private _currentChunkHighlight?: L.Rectangle;
    private readonly CHUNK_SIZE: number = 16;
    private _selectedChunks: Set<string> = new Set();
    private _selectedRectangles: Map<string, L.Rectangle> = new Map();
    private _availableChunks: Set<string> = new Set();
    private _removableChunks: Set<string> = new Set(); // Chunks that can be deselected
    private _ownedChunks: Set<string> = new Set(); // Already purchased chunks
    private _deleteMode: boolean = false; // When true, clicking a deletable OWNED chunk stages it for removal
    private _deletableChunks: Set<string> = new Set(); // Owned chunks currently eligible to be staged for deletion
    private _privateEditMode: boolean = false; // When true, clicking an eligible OWNED chunk stages an isPrivate toggle
    private _privateEditableChunks: Set<string> = new Set(); // Owned chunks currently eligible to have isPrivate toggled
    private _marginChunks: Set<string> = new Set(); // Buffer zone around other regions (blocked)
    private _allowAllChunks: boolean = false; // When true, any chunk can be clicked
    private _singleSelect: boolean = false; // When true, selecting a chunk replaces the previous one

    // Per-chunk colour overlay + markup tooltips (rendered for the CURRENT world,
    // independent of whether the selection grid is active). Payloads carry every
    // world; we filter by the world this control was created for.
    private _coloredChunks: Array<{
        x: number;
        z: number;
        color: string;
        world: string;
        owned?: boolean;
        regionId?: number;
    }> = [];
    private _chunkMarkups: Array<{ x: number; z: number; title: string; description: string | null; world: string }> =
        [];
    private _markupByKey: Map<string, { title: string; description: string | null }> = new Map();
    private _highlightChunks: Array<{ x: number; z: number }> = [];

    // Paint mode: clicking one of the paintable chunks emits a single
    // 'pl3xmap:chunk-clicked' message to the parent instead of toggling the cart.
    private _paintMode: boolean = false;
    private _paintableChunks: Set<string> = new Set();

    // Markup-view mode: replaces per-chunk hex colours with faint white +
    // per-zone random colours while the user is assigning chunks to zones.
    private _markupViewLayer?: L.LayerGroup;
    private _markupViewEnabled: boolean = false;
    private _markupViewChunks: Array<{ x: number; z: number; markupId: number | null }> = [];
    private _markupViewColors: Record<string, string> = {};

    // Always-on passive hover used to show markup tooltips when the grid is off.
    private _passiveBound: boolean = false;
    private _passiveHighlight?: L.Rectangle;

    // Player-placed map markers (buildings, landmarks). Rendered as fixed-size
    // pins that do not scale with zoom, independent of any panel/edit mode.
    private _markerLayer?: L.LayerGroup;
    private _mapMarkers: Array<{ id: number; name: string; type: string; x: number; z: number; world: string }> = [];
    // Marker type -> SVG markup, supplied by the parent. The map stays agnostic
    // of the type list: an unknown type just falls back to the default glyph.
    private _markerIcons: Record<string, string> = {};
    // Driven by MarkerToggleControl (which outlives this control, so the user's
    // choice carries over when the world changes).
    private _markersVisible: boolean = true;
    // Marker the parent currently has open; rendered in the highlight colour.
    private _selectedMarkerId: number | null = null;

    // Persistent badge marking chunks with isPrivate === true. Visible in
    // every mode/view (like coloured chunks and map markers) — independent of
    // whether the grid is active or whose region it is.
    private _privateMarkerLayer?: L.LayerGroup;
    private _privateChunkMarkers: Array<{ x: number; z: number; world: string }> = [];
    // Same data as _privateChunkMarkers, filtered to the current world and
    // keyed for O(1) lookup — used to tell "will become private" from "will
    // become public" while hovering/selecting in private-edit mode.
    private _privateChunkKeys: Set<string> = new Set();

    private readonly _messageHandler: (e: MessageEvent) => void;
    private readonly _keydownHandler: (e: KeyboardEvent) => void;

    constructor(pl3xmap: Pl3xMap, position: string) {
        super(pl3xmap, position);

        this._markersVisible = pl3xmap.controlManager.markerToggleControl?.markersVisible ?? true;

        // Listen for messages from parent window
        this._messageHandler = (e: MessageEvent) => {
            if (e.data?.type === "pl3xmap:clear-selection") {
                this.clearSelection();
            } else if (e.data?.type === "pl3xmap:center-on") {
                // Center map on specific coordinates, keep current zoom if not specified
                const { x, z, zoom } = e.data;
                this._pl3xmap.map.centerOn(x, z, zoom ?? this._pl3xmap.map.getCurrentZoom());
            } else if (e.data?.type === "pl3xmap:set-world") {
                // Switch to another world, optionally landing on given coords.
                // The parent uses this to follow a marker that lives in a
                // world other than the one currently open.
                this.switchWorld(e.data.world, e.data.x, e.data.z, e.data.zoom);
            } else if (e.data?.type === "pl3xmap:request-world") {
                // The parent asks which world is open (its listener may have
                // been attached after our control-ready went out).
                this.postWorldChanged();
            } else if (e.data?.type === "pl3xmap:enable-grid") {
                this.enableGrid();
            } else if (e.data?.type === "pl3xmap:disable-grid") {
                this.disableGrid();
            } else if (e.data?.type === "pl3xmap:set-available-chunks") {
                // Receive available chunks from parent
                const chunks: ChunkCoordinates[] = e.data.chunks || [];
                this._availableChunks.clear();
                this._allowAllChunks = false;
                for (const chunk of chunks) {
                    this._availableChunks.add(`${chunk.x},${chunk.z}`);
                }
                this.redrawAvailableChunks();
            } else if (e.data?.type === "pl3xmap:set-allow-all-chunks") {
                // Allow clicking any chunk (for new region creation)
                this._allowAllChunks = e.data.allow === true;
                this._availableChunks.clear();
                this.redrawAvailableChunks();
            } else if (e.data?.type === "pl3xmap:set-single-select") {
                // When enabled, selecting a chunk replaces the previously selected one
                this._singleSelect = e.data.enabled === true;
            } else if (e.data?.type === "pl3xmap:set-removable-chunks") {
                // Receive removable chunks from parent (chunks that can be deselected)
                const chunks: ChunkCoordinates[] = e.data.chunks || [];
                this._removableChunks.clear();
                for (const chunk of chunks) {
                    this._removableChunks.add(`${chunk.x},${chunk.z}`);
                }
                this.redrawSelectedChunks(); // Redraw to update visual state
            } else if (e.data?.type === "pl3xmap:set-owned-chunks") {
                // Receive already purchased chunks from parent (display in white)
                const chunks: ChunkCoordinates[] = e.data.chunks || [];
                this._ownedChunks.clear();
                for (const chunk of chunks) {
                    this._ownedChunks.add(`${chunk.x},${chunk.z}`);
                }
                this.redrawOwnedChunks();
            } else if (e.data?.type === "pl3xmap:set-margin-chunks") {
                // Receive the buffer zone around other regions (orange, blocked)
                const chunks: ChunkCoordinates[] = e.data.chunks || [];
                this._marginChunks.clear();
                for (const chunk of chunks) {
                    this._marginChunks.add(`${chunk.x},${chunk.z}`);
                }
                this.redrawMarginChunks();
            } else if (e.data?.type === "pl3xmap:set-colored-chunks") {
                // Persistent per-chunk colours for every world; filtered on draw.
                this._coloredChunks = e.data.chunks || [];
                this.redrawColoredChunks();
                this.ensurePassiveHover();
            } else if (e.data?.type === "pl3xmap:set-chunk-markups") {
                // Per-chunk zone metadata used for hover tooltips.
                this._chunkMarkups = e.data.chunks || [];
                this.rebuildMarkupIndex();
                this.ensurePassiveHover();
            } else if (e.data?.type === "pl3xmap:set-private-chunk-markers") {
                // isPrivate chunks; always visible regardless of edit mode.
                this._privateChunkMarkers = e.data.chunks || [];
                this.redrawPrivateChunkMarkers();
            } else if (e.data?.type === "pl3xmap:set-marker-icons") {
                // Per-type glyphs owned by the parent app; redraw so existing
                // pins pick up icons that arrived after them.
                this._markerIcons = e.data.icons || {};
                this.redrawMapMarkers();
            } else if (e.data?.type === "pl3xmap:set-map-markers") {
                // Player-placed markers; always visible regardless of edit mode.
                this._mapMarkers = e.data.markers || [];
                this.redrawMapMarkers();
            } else if (e.data?.type === "pl3xmap:set-selected-marker") {
                // Which marker the parent has open; null/absent clears it.
                this.setSelectedMarker(e.data.markerId ?? null);
            } else if (e.data?.type === "pl3xmap:highlight-markup") {
                // Highlight the chunks of a single zone (empty array clears).
                this._highlightChunks = e.data.chunks || [];
                this.redrawHighlightChunks();
            } else if (e.data?.type === "pl3xmap:set-markup-view") {
                // Enter/exit markup-view mode: replace per-chunk hex colours with
                // faint white (unassigned) + per-zone colours (assigned).
                this._markupViewEnabled = e.data.enabled === true;
                this._markupViewChunks = e.data.regionChunks || [];
                this._markupViewColors = e.data.markupColors || {};
                this.redrawMarkupView();
                this.redrawColoredChunks(); // hidden while markup view is on
            } else if (e.data?.type === "pl3xmap:set-paint-mode") {
                this._paintMode = e.data.enabled === true;
                this._paintableChunks.clear();
                const chunks: ChunkCoordinates[] = e.data.chunks || [];
                for (const chunk of chunks) {
                    this._paintableChunks.add(`${chunk.x},${chunk.z}`);
                }
            } else if (e.data?.type === "pl3xmap:set-delete-mode") {
                // Owned chunks the parent has recomputed as currently safe to stage
                // for deletion (removing them wouldn't break the region's shape).
                this._deleteMode = e.data.enabled === true;
                this._deletableChunks.clear();
                const chunks: ChunkCoordinates[] = e.data.chunks || [];
                for (const chunk of chunks) {
                    this._deletableChunks.add(`${chunk.x},${chunk.z}`);
                }
                this.redrawAvailableChunks();
            } else if (e.data?.type === "pl3xmap:set-private-edit-mode") {
                // Owned chunks the parent has recomputed as currently safe to stage
                // for an isPrivate toggle (mark or unmark, depending on the chunk's
                // current state — the parent decides which; we just gate clicks).
                this._privateEditMode = e.data.enabled === true;
                this._privateEditableChunks.clear();
                const chunks: ChunkCoordinates[] = e.data.chunks || [];
                for (const chunk of chunks) {
                    this._privateEditableChunks.add(`${chunk.x},${chunk.z}`);
                }
                this.redrawAvailableChunks();
            } else if (e.data?.type === "pl3xmap:set-selection") {
                // Replace the current selection cart with the given chunks.
                this.applySelection(e.data.chunks || []);
            }
        };
        window.addEventListener("message", this._messageHandler);

        // The embedding page (cybercore-frontend) cycles its navbar tabs with
        // 1/3, but that listener lives on the parent document — keydowns
        // happening inside this iframe never reach it. Forward the raw key
        // so the parent can replay it on its own document.
        this._keydownHandler = (e: KeyboardEvent) => {
            if (e.key !== "1" && e.key !== "3") {
                return;
            }
            if (e.ctrlKey || e.metaKey || e.altKey) {
                return;
            }

            const target = e.target as HTMLElement | null;
            if (target?.isContentEditable || target?.tagName === "INPUT" || target?.tagName === "TEXTAREA") {
                return;
            }

            if (window.parent !== window) {
                window.parent.postMessage({ type: "pl3xmap:navbar-hotkey", key: e.key }, "*");
            }
        };
        document.addEventListener("keydown", this._keydownHandler);

        // Announce readiness so the parent (re)sends the persistent overlays.
        // A fresh control is created on every world switch, so this also keeps
        // the colour/markup overlays in sync when the user changes worlds.
        if (window.parent !== window) {
            window.parent.postMessage({ type: "pl3xmap:control-ready", world: this.currentWorldName() }, "*");
            // Same event, separate message: the parent tracks the open world
            // even when it ignores control-ready (e.g. the user clicked a
            // world in the sidebar, not in our UI).
            this.postWorldChanged();
        }
    }

    /** Tell the parent which world is currently open. */
    private postWorldChanged(): void {
        if (window.parent === window) {
            return;
        }

        window.parent.postMessage({ type: "pl3xmap:world-changed", world: this.currentWorldName() }, "*");
    }

    /**
     * Switch to another world by its folder name, then centre on the given
     * block coordinates. Unknown worlds are ignored; the already-open world
     * only re-centres (setWorld would bail out early on it).
     */
    private switchWorld(worldName: string, x?: number, z?: number, zoom?: number): void {
        const worldManager = this._pl3xmap.worldManager;
        const world = worldManager.getWorld(worldName);

        if (!world) {
            return;
        }

        const centre = (): void => {
            if (typeof x === "number" && typeof z === "number") {
                this._pl3xmap.map.centerOn(x, z, zoom ?? this._pl3xmap.map.getCurrentZoom());
            }
        };

        if (world === worldManager.currentWorld) {
            centre();

            return;
        }

        // setWorld() rebuilds the controls (including this one) and centres on
        // the world's own spawn — our centring has to come after that.
        void worldManager.setWorld(world).then(centre);
    }

    onAdd(_map: Pl3xMapLeafletMap): HTMLElement {
        // Return empty hidden element for Leaflet
        const dummy = L.DomUtil.create("div");
        dummy.style.display = "none";
        return dummy;
    }

    onRemove(map: Pl3xMapLeafletMap): void {
        // Clear selection and notify parent before removing
        this.clearSelection();

        if (this._gridLayer) {
            this._gridLayer.remove();
        }
        if (this._selectionLayer) {
            this._selectionLayer.remove();
        }
        if (this._availableLayer) {
            this._availableLayer.remove();
        }
        if (this._ownedLayer) {
            this._ownedLayer.remove();
        }
        if (this._marginLayer) {
            this._marginLayer.remove();
        }
        if (this._coloredLayer) {
            this._coloredLayer.remove();
        }
        if (this._highlightLayer) {
            this._highlightLayer.remove();
        }
        if (this._markupViewLayer) {
            this._markupViewLayer.remove();
        }
        if (this._regionBorderLayer) {
            this._regionBorderLayer.remove();
        }
        if (this._markerLayer) {
            this._markerLayer.remove();
        }
        if (this._privateMarkerLayer) {
            this._privateMarkerLayer.remove();
        }
        if (this._currentChunkHighlight) {
            this._currentChunkHighlight.remove();
        }
        if (this._passiveHighlight) {
            this._passiveHighlight.remove();
        }
        map.off("mousemove", this.onMouseMove);
        map.off("mouseout", this.onMouseOut);
        map.off("click", this.onMapClick);
        map.off("moveend", this.updateGrid);
        map.off("zoomend", this.updateGrid);
        map.off("mousemove", this.onPassiveMove);
        map.off("mouseout", this.onPassiveOut);
        map.off("zoomend", this.onZoomRestyle);

        // Remove message listener
        window.removeEventListener("message", this._messageHandler);
        document.removeEventListener("keydown", this._keydownHandler);
    }

    /**
     * Enable the chunk grid for selection
     * Called via postMessage from parent window
     */
    public enableGrid(): void {
        if (this._isGridActive) {
            return;
        }

        this._isGridActive = true;
        const map = this._pl3xmap.map;

        // Owned chunks layer (bottom - white)
        if (!this._ownedLayer) {
            this._ownedLayer = L.layerGroup().addTo(map);
        }

        // Available chunks layer
        if (!this._availableLayer) {
            this._availableLayer = L.layerGroup().addTo(map);
        }

        // Margin (buffer zone) layer
        if (!this._marginLayer) {
            this._marginLayer = L.layerGroup().addTo(map);
        }

        if (!this._gridLayer) {
            this._gridLayer = L.layerGroup().addTo(map);
        }

        // Selection layer on top
        if (!this._selectionLayer) {
            this._selectionLayer = L.layerGroup().addTo(map);
        }

        this.updateGrid();
        this.redrawOwnedChunks();
        this.redrawAvailableChunks();
        this.redrawMarginChunks();
        this.redrawSelectedChunks();
        this.redrawColoredChunks(); // re-render with borders now that grid is on
        map.on("moveend", this.updateGrid);
        map.on("zoomend", this.updateGrid);
        map.on("mousemove", this.onMouseMove);
        map.on("mouseout", this.onMouseOut);
        map.on("click", this.onMapClick);
    }

    /**
     * Disable the chunk grid
     * Called via postMessage from parent window
     */
    public disableGrid(): void {
        if (!this._isGridActive) {
            return;
        }

        this._isGridActive = false;
        const map = this._pl3xmap.map;

        // Clear selection and available chunks when hiding grid
        this.clearSelection();
        this._availableChunks.clear();
        this._removableChunks.clear();
        this._ownedChunks.clear();
        this._marginChunks.clear();
        this._allowAllChunks = false;
        this._singleSelect = false;
        this._paintMode = false;
        this._paintableChunks.clear();
        this._deleteMode = false;
        this._deletableChunks.clear();
        this._privateEditMode = false;
        this._privateEditableChunks.clear();
        this._markupViewEnabled = false;
        if (this._markupViewLayer) {
            this._markupViewLayer.clearLayers();
        }

        if (this._gridLayer) {
            this._gridLayer.clearLayers();
        }

        if (this._selectionLayer) {
            this._selectionLayer.clearLayers();
        }

        if (this._availableLayer) {
            this._availableLayer.clearLayers();
        }

        if (this._ownedLayer) {
            this._ownedLayer.clearLayers();
        }

        if (this._marginLayer) {
            this._marginLayer.clearLayers();
        }

        if (this._currentChunkHighlight) {
            this._currentChunkHighlight.remove();
            this._currentChunkHighlight = undefined;
        }

        this.redrawColoredChunks(); // re-render without borders now that grid is off

        map.off("moveend", this.updateGrid);
        map.off("zoomend", this.updateGrid);
        map.off("mousemove", this.onMouseMove);
        map.off("mouseout", this.onMouseOut);
        map.off("click", this.onMapClick);
    }

    private updateGrid = (): void => {
        if (!this._gridLayer || !this._isGridActive) {
            return;
        }

        const map = this._pl3xmap.map;
        this._gridLayer.clearLayers();

        const bounds = map.getBounds();
        const zoom = map.getZoom();

        // Only show grid at certain zoom levels for performance
        if (zoom < 1) {
            return;
        }

        // Convert bounds to chunk coordinates
        const minLat = bounds.getSouth();
        const maxLat = bounds.getNorth();
        const minLng = bounds.getWest();
        const maxLng = bounds.getEast();

        // Convert to world coordinates
        const minX = Math.floor(minLng / pixelsToMeters(this.CHUNK_SIZE)) * this.CHUNK_SIZE;
        const maxX = Math.ceil(maxLng / pixelsToMeters(this.CHUNK_SIZE)) * this.CHUNK_SIZE;
        const minZ = Math.floor(minLat / pixelsToMeters(this.CHUNK_SIZE)) * this.CHUNK_SIZE;
        const maxZ = Math.ceil(maxLat / pixelsToMeters(this.CHUNK_SIZE)) * this.CHUNK_SIZE;

        // Calculate total chunks in view
        const chunksX = Math.ceil((maxX - minX) / this.CHUNK_SIZE);
        const chunksZ = Math.ceil((maxZ - minZ) / this.CHUNK_SIZE);
        const totalChunks = chunksX * chunksZ;

        // Dynamic limit based on zoom - more chunks at higher zoom
        const maxChunks = Math.min(10000, Math.max(2000, zoom * 1000));

        // If too many chunks, don't render grid (only show at closer zoom)
        if (totalChunks > maxChunks) {
            return;
        }

        // Draw chunk grid
        for (let x = minX; x < maxX; x += this.CHUNK_SIZE) {
            for (let z = minZ; z < maxZ; z += this.CHUNK_SIZE) {
                const chunkBounds: L.LatLngBoundsLiteral = [
                    [pixelsToMeters(z), pixelsToMeters(x)],
                    [pixelsToMeters(z + this.CHUNK_SIZE), pixelsToMeters(x + this.CHUNK_SIZE)],
                ];

                const rect = L.rectangle(chunkBounds, {
                    color: "#888888",
                    weight: 0.5,
                    fill: false,
                    interactive: false,
                    className: "chunk-grid-line",
                });

                rect.addTo(this._gridLayer);
            }
        }
    };

    /**
     * Is `chunkKey` clickable right now? Delete and private-edit modes each
     * repurpose "available" to mean "OWNED chunk currently eligible for that
     * action" (recomputed by the parent after every selection change) instead
     * of "unowned chunk eligible for purchase". Exactly one of these modes is
     * ever active at a time.
     */
    private isChunkAvailable(chunkKey: string): boolean {
        if (this._deleteMode) {
            return this._deletableChunks.has(chunkKey);
        }
        if (this._privateEditMode) {
            return this._privateEditableChunks.has(chunkKey);
        }
        return this._allowAllChunks || this._availableChunks.has(chunkKey);
    }

    /** Delete/private-edit "available" chunks ARE owned — the ownership block
     * has to be bypassed for them, unlike plain buy-mode availability. */
    private isSpecialEditMode(): boolean {
        return this._deleteMode || this._privateEditMode;
    }

    private onMouseMove = (e: L.LeafletMouseEvent): void => {
        if (!this._isGridActive) {
            return;
        }

        const map = this._pl3xmap.map;
        const latlng = e.latlng;

        // Calculate chunk coordinates
        const chunkX = Math.floor(latlng.lng / pixelsToMeters(this.CHUNK_SIZE)) * this.CHUNK_SIZE;
        const chunkZ = Math.floor(latlng.lat / pixelsToMeters(this.CHUNK_SIZE)) * this.CHUNK_SIZE;

        const chunkKey = this.getChunkKey(chunkX, chunkZ);
        const isSelected = this._selectedChunks.has(chunkKey);
        const isAvailable = this.isChunkAvailable(chunkKey);
        const isRemovable = this._removableChunks.has(chunkKey);
        const isOwned = this._ownedChunks.has(chunkKey) && !(this.isSpecialEditMode() && isAvailable);
        const isMargin = this._marginChunks.has(chunkKey);

        // Create or update highlight
        if (this._currentChunkHighlight) {
            this._currentChunkHighlight.remove();
        }

        // Different color for hover depending on state
        let hoverColor: string;
        let tooltipText: string;
        let canInteract: boolean;

        if (isOwned) {
            hoverColor = "#ffffff"; // White for owned
            tooltipText = "Already purchased";
            canInteract = false;
        } else if (isMargin) {
            hoverColor = "#ff9800"; // Orange for the buffer zone
            tooltipText = "Too close to another region";
            canInteract = false;
        } else if (isSelected) {
            if (isRemovable) {
                if (this._deleteMode) {
                    hoverColor = "#ff1744"; // Strong red when staged for deletion
                    tooltipText = "Click to unstage";
                } else if (this._privateEditMode) {
                    hoverColor = "#ff6b6b"; // Light red when staged for a privacy toggle (click to unstage)
                    tooltipText = "Click to unstage";
                } else {
                    hoverColor = "#ff6b6b"; // Light red for removable selected
                    tooltipText = "Click to deselect";
                }
                canInteract = true;
            } else {
                hoverColor = "#888888"; // Gray for non-removable selected (locked)
                tooltipText = "Cannot remove (would break region)";
                canInteract = false;
            }
        } else if (isAvailable) {
            if (this._deleteMode) {
                hoverColor = "#ff8a80"; // Soft red for deletable
                tooltipText = "Click to stage for deletion";
            } else if (this._privateEditMode) {
                const willBecomePrivate = !this._privateChunkKeys.has(chunkKey);
                // Mirrors buy-mode's clear signalling, but two-tone: bright blue
                // to go private, bright amber to go public.
                hoverColor = willBecomePrivate ? "#40c4ff" : "#ffca28";
                tooltipText = willBecomePrivate ? "Click to make private" : "Click to make public";
            } else {
                hoverColor = "#ffff00"; // Yellow for available
                tooltipText = "Click to select";
            }
            canInteract = true;
        } else {
            hoverColor = "#ff0000"; // Red for unavailable
            tooltipText = "";
            canInteract = false;
        }

        this._currentChunkHighlight = this.createChunkRect(chunkX, chunkZ, {
            color: hoverColor,
            fillOpacity: canInteract ? 0.3 : 0.15,
            weightScale: 1.5,
            modifier: "hover",
        }).addTo(map);

        // Add tooltip with chunk coordinates (+ zone info when present)
        const worldChunkX = Math.floor(chunkX / this.CHUNK_SIZE);
        const worldChunkZ = Math.floor(chunkZ / this.CHUNK_SIZE);
        this._currentChunkHighlight
            .bindTooltip(`Chunk: ${worldChunkX}, ${worldChunkZ}<br>${tooltipText}${this.markupTooltipHtml(chunkKey)}`, {
                permanent: false,
                direction: "top",
            })
            .openTooltip();
    };

    private onMouseOut = (): void => {
        if (this._currentChunkHighlight) {
            this._currentChunkHighlight.remove();
            this._currentChunkHighlight = undefined;
        }
    };

    private onMapClick = (e: L.LeafletMouseEvent): void => {
        if (!this._isGridActive) {
            return;
        }

        // Marker placement owns the click that ends it — don't also touch the cart.
        if (this._pl3xmap.controlManager.markerPlacementControl?.isPlacing) {
            return;
        }

        const latlng = e.latlng;

        // Calculate chunk coordinates
        const chunkX = Math.floor(latlng.lng / pixelsToMeters(this.CHUNK_SIZE)) * this.CHUNK_SIZE;
        const chunkZ = Math.floor(latlng.lat / pixelsToMeters(this.CHUNK_SIZE)) * this.CHUNK_SIZE;

        const chunkKey = this.getChunkKey(chunkX, chunkZ);
        const isSelected = this._selectedChunks.has(chunkKey);
        const isAvailable = this.isChunkAvailable(chunkKey);
        const isRemovable = this._removableChunks.has(chunkKey);
        const isOwned = this._ownedChunks.has(chunkKey) && !(this.isSpecialEditMode() && isAvailable);

        // Paint mode: clicking a paintable chunk recolours it (handled by parent).
        if (this._paintMode) {
            if (this._paintableChunks.has(chunkKey)) {
                const [px, pz] = chunkKey.split(",").map(Number);
                this.sendChunkClickedToParent(px, pz);
            }
            return;
        }

        // Can't interact with owned chunks
        if (isOwned) {
            return;
        }

        // Can't build within the buffer zone around other regions
        if (this._marginChunks.has(chunkKey)) {
            return;
        }

        // For selected chunks, only allow removal if it's in removable list
        if (isSelected) {
            if (!isRemovable) {
                return; // Can't remove this chunk
            }
            this.toggleChunkSelection(chunkX, chunkZ);
            return;
        }

        // For unselected chunks, only allow selection if available
        if (!isAvailable) {
            return;
        }

        this.toggleChunkSelection(chunkX, chunkZ);
    };

    private toggleChunkSelection(chunkX: number, chunkZ: number): void {
        const chunkKey = this.getChunkKey(chunkX, chunkZ);

        if (this._selectedChunks.has(chunkKey)) {
            // Remove from selection
            this._selectedChunks.delete(chunkKey);
            const rect = this._selectedRectangles.get(chunkKey);
            if (rect) {
                rect.remove();
                this._selectedRectangles.delete(chunkKey);
            }
        } else {
            // In single-select mode, replace any existing selection first
            if (this._singleSelect) {
                this._selectedChunks.clear();
                this._selectedRectangles.clear();
                if (this._selectionLayer) {
                    this._selectionLayer.clearLayers();
                }
            }

            // Add to selection
            this._selectedChunks.add(chunkKey);
            this.drawSelectedChunk(chunkX, chunkZ, chunkKey);
        }

        this.notifySelectionChange();
    }

    private drawSelectedChunk(chunkX: number, chunkZ: number, chunkKey: string): void {
        if (!this._selectionLayer) {
            return;
        }

        // Get chunk coordinates for checking removable/privacy status
        const worldChunkX = Math.floor(chunkX / this.CHUNK_SIZE);
        const worldChunkZ = Math.floor(chunkZ / this.CHUNK_SIZE);
        const selectedKey = `${worldChunkX},${worldChunkZ}`;
        const isRemovable = this._removableChunks.has(selectedKey);

        // Removable picks are green (red in delete mode, purple in private-edit
        // mode); locked (would break the region) are grey.
        let style: { color: string; fillOpacity: number; opacity: number; modifier: string };
        if (!isRemovable) {
            style = { color: "#9e9e9e", fillOpacity: 0.35, opacity: 0.9, modifier: "selected-locked" };
        } else if (this._deleteMode) {
            style = { color: "#ff1744", fillOpacity: 0.45, opacity: 0.95, modifier: "selected-delete" };
        } else if (this._privateEditMode) {
            style = { color: "#00e676", fillOpacity: 0.4, opacity: 0.95, modifier: "selected-private" };
        } else {
            style = { color: "#00e676", fillOpacity: 0.4, opacity: 0.95, modifier: "selected" };
        }

        const rect = this.createChunkRect(chunkX, chunkZ, style).addTo(this._selectionLayer);

        this._selectedRectangles.set(chunkKey, rect);
    }

    private redrawSelectedChunks(): void {
        if (!this._selectionLayer) {
            return;
        }

        this._selectionLayer.clearLayers();
        this._selectedRectangles.clear();

        this._selectedChunks.forEach((chunkKey) => {
            const [chunkX, chunkZ] = this.parseChunkKey(chunkKey);
            this.drawSelectedChunk(chunkX, chunkZ, chunkKey);
        });
    }

    /**
     * Redraw owned chunks on the map
     * Owned chunks are NOT displayed visually, but are tracked for interaction prevention
     */
    private redrawOwnedChunks(): void {
        if (!this._ownedLayer) {
            return;
        }

        // Clear any existing visuals - owned chunks are not displayed
        this._ownedLayer.clearLayers();
    }

    /**
     * Redraw available chunks on the map
     * Available chunks are displayed as blue highlighted areas
     */
    private redrawAvailableChunks(): void {
        if (!this._availableLayer) {
            return;
        }

        this._availableLayer.clearLayers();

        // Delete/private-edit modes show which OWNED chunks are currently safe
        // to stage for that action instead of which new chunks are buyable.
        const chunkKeys = this._deleteMode
            ? this._deletableChunks
            : this._privateEditMode
              ? this._privateEditableChunks
              : this._availableChunks;

        for (const chunkKey of chunkKeys) {
            const [x, z] = chunkKey.split(",").map(Number);
            const chunkX = x * this.CHUNK_SIZE;
            const chunkZ = z * this.CHUNK_SIZE;

            // .chunk-cell--available pulses via CSS to signal "buyable"/"deletable"/"privatable".
            let style: { color: string; fillOpacity: number; opacity: number; modifier: string };
            if (this._deleteMode) {
                style = { color: "#ff8a80", fillOpacity: 0.22, opacity: 0.8, modifier: "deletable" };
            } else if (this._privateEditMode) {
                const willBecomePrivate = !this._privateChunkKeys.has(chunkKey);
                // Two-tone, buy-mode-style signalling: blue = eligible to go
                // private, amber = eligible to go public.
                style = {
                    color: willBecomePrivate ? "#00bfff" : "#ffb300",
                    fillOpacity: 0.22,
                    opacity: 0.8,
                    modifier: "privatable",
                };
            } else {
                style = { color: "#00bfff", fillOpacity: 0.25, opacity: 0.8, modifier: "available" };
            }

            this.createChunkRect(chunkX, chunkZ, style).addTo(this._availableLayer);
        }
    }

    /**
     * Redraw the margin (buffer zone) around other regions as orange areas
     */
    private redrawMarginChunks(): void {
        if (!this._marginLayer) {
            return;
        }

        this._marginLayer.clearLayers();

        for (const chunkKey of this._marginChunks) {
            const [x, z] = chunkKey.split(",").map(Number);
            const chunkX = x * this.CHUNK_SIZE;
            const chunkZ = z * this.CHUNK_SIZE;

            this.createChunkRect(chunkX, chunkZ, {
                color: "#ff9800", // Orange buffer zone
                fillOpacity: 0.2,
                opacity: 0.8,
                modifier: "margin",
            }).addTo(this._marginLayer);
        }
    }

    /**
     * Clear all selected chunks
     * Can be called from parent window via postMessage
     */
    public clearSelection(): void {
        this._selectedChunks.clear();
        this._selectedRectangles.clear();
        if (this._selectionLayer) {
            this._selectionLayer.clearLayers();
        }
        this.notifySelectionChange();
    }

    private notifySelectionChange(): void {
        // Send postMessage to parent window
        this.sendSelectionToParent();
    }

    /**
     * Send chunk selection data to parent window via postMessage
     * Used when map is embedded in iframe
     */
    private sendSelectionToParent(): void {
        const chunks = this.getSelectedChunks();
        const count = chunks.length;
        const pricePerChunk = 1; // Placeholder, real price calculated on backend
        const worldName = this._pl3xmap.worldManager.currentWorld?.name || "world";

        const message: ChunkSelectionMessage = {
            type: "pl3xmap:chunk-selection",
            data: {
                chunks,
                count,
                price: count * pricePerChunk,
                worldName,
            },
        };

        // Send to parent window (for iframe embedding)
        if (window.parent !== window) {
            window.parent.postMessage(message, "*");
        }

        // Also dispatch custom event for same-window usage
        window.dispatchEvent(
            new CustomEvent("pl3xmap:chunk-selection", {
                detail: message.data,
            }),
        );
    }

    private getChunkKey(chunkX: number, chunkZ: number): string {
        const worldChunkX = Math.floor(chunkX / this.CHUNK_SIZE);
        const worldChunkZ = Math.floor(chunkZ / this.CHUNK_SIZE);
        return `${worldChunkX},${worldChunkZ}`;
    }

    private parseChunkKey(key: string): [number, number] {
        const [x, z] = key.split(",").map(Number);
        return [x * this.CHUNK_SIZE, z * this.CHUNK_SIZE];
    }

    public getSelectedChunks(): ChunkCoordinates[] {
        const chunks: ChunkCoordinates[] = [];
        this._selectedChunks.forEach((chunkKey) => {
            const [x, z] = this.parseChunkKey(chunkKey);
            chunks.push({
                x: Math.floor(x / this.CHUNK_SIZE),
                z: Math.floor(z / this.CHUNK_SIZE),
            });
        });
        return chunks;
    }

    /** Folder-name of the world this control is rendering. */
    private currentWorldName(): string {
        return this._pl3xmap.worldManager.currentWorld?.name || "";
    }

    /** On-screen size, in pixels, of one chunk at the current zoom. */
    private chunkScreenPx(): number {
        const map = this._pl3xmap.map;
        const a = map.latLngToLayerPoint(L.latLng(pixelsToMeters(0), pixelsToMeters(0)));
        const b = map.latLngToLayerPoint(L.latLng(pixelsToMeters(0), pixelsToMeters(this.CHUNK_SIZE)));
        return Math.abs(b.x - a.x);
    }

    /**
     * Border width scaled to the on-screen size of a chunk, so the stroke stays a
     * constant FRACTION of the chunk at every zoom (it never looks thick when
     * zoomed out). Clamped so it never disappears or gets too heavy.
     */
    private chunkStrokeWeight(): number {
        return Math.max(0.4, Math.min(1.5, this.chunkScreenPx() * 0.03));
    }

    /**
     * Inset (in map units) that pulls a chunk rectangle inward by `weight` screen
     * pixels per side. Because the stroke is centred on the rectangle edge, this
     * keeps neighbouring chunks' borders from overlapping at the shared edge.
     */
    private chunkInset(weight: number): number {
        const chunkPx = this.chunkScreenPx();
        if (chunkPx <= 0) {
            return 0;
        }
        const metersPerPixel = (pixelsToMeters(this.CHUNK_SIZE) - pixelsToMeters(0)) / chunkPx;
        return weight * metersPerPixel;
    }

    /** Chunk bounds shrunk by `inset` map units on every side. */
    private insetChunkBounds(chunkX: number, chunkZ: number, inset: number): L.LatLngBoundsLiteral {
        return [
            [pixelsToMeters(chunkZ) + inset, pixelsToMeters(chunkX) + inset],
            [pixelsToMeters(chunkZ + this.CHUNK_SIZE) - inset, pixelsToMeters(chunkX + this.CHUNK_SIZE) - inset],
        ];
    }

    /**
     * Single source of truth for rendering a chunk cell. Every chunk overlay
     * (available, selected, margin, coloured, highlight, hover) is built here so
     * they all share identical geometry: a zoom-scaled border that's inset so
     * neighbouring cells never overlap. Callers only vary colour/opacity/modifier.
     * `chunkX`/`chunkZ` are block coordinates (chunk index * CHUNK_SIZE).
     */
    private createChunkRect(
        chunkX: number,
        chunkZ: number,
        style: {
            color: string;
            fillColor?: string;
            fillOpacity?: number;
            opacity?: number;
            weightScale?: number;
            stroke?: boolean;
            fill?: boolean;
            modifier: string;
        },
    ): L.Rectangle {
        const hasStroke = style.stroke !== false;
        const weight = hasStroke ? this.chunkStrokeWeight() * (style.weightScale ?? 1) : 0;
        const inset = this.chunkInset(weight);
        const bounds = this.insetChunkBounds(chunkX, chunkZ, inset);

        return L.rectangle(bounds, {
            interactive: false, // chunk overlays never swallow map clicks / panning
            stroke: hasStroke,
            color: style.color,
            weight,
            opacity: style.opacity ?? 1,
            fill: style.fill !== false,
            fillColor: style.fillColor ?? style.color,
            fillOpacity: style.fillOpacity ?? 0,
            className: `chunk-cell chunk-cell--${style.modifier}`,
        });
    }

    /** Re-render the zoom-scaled overlays after a zoom change. */
    private onZoomRestyle = (): void => {
        this.redrawColoredChunks();
        this.redrawHighlightChunks();
        this.redrawAvailableChunks();
        this.redrawMarkupView();
    };

    /** HTML snippet appended to chunk tooltips when the chunk belongs to a zone. */
    private markupTooltipHtml(chunkKey: string): string {
        const markup = this._markupByKey.get(chunkKey);
        if (!markup) {
            return "";
        }
        const desc = markup.description ? `<br><span style="opacity:0.8">${markup.description}</span>` : "";
        return `<br><b style="color:#05d9e8">${markup.title}</b>${desc}`;
    }

    /** Rebuild the per-chunk markup lookup for the current world. */
    private rebuildMarkupIndex(): void {
        const world = this.currentWorldName();
        this._markupByKey.clear();
        for (const m of this._chunkMarkups) {
            if (m.world === world) {
                this._markupByKey.set(`${m.x},${m.z}`, { title: m.title, description: m.description });
            }
        }
    }

    /** Render every owned/occupied chunk in its own colour (current world). */
    private redrawColoredChunks(): void {
        if (!this._coloredLayer) {
            this._coloredLayer = L.layerGroup().addTo(this._pl3xmap.map);
        }
        this._coloredLayer.clearLayers();

        // Markup view takes over rendering when active — coloured chunks are hidden.
        if (this._markupViewEnabled) {
            return;
        }

        const world = this.currentWorldName();
        // Borders only show in grid mode (stroke=false otherwise). The helper
        // insets the rectangle so neighbouring borders never overlap; with the
        // stroke off the inset is 0, so fills stay flush and adjacent chunks read
        // as one solid area. Own chunks get a slightly denser fill, but foreign
        // regions stay opaque enough to be spotted at a glance.
        const gridOn = this._isGridActive;
        for (const chunk of this._coloredChunks) {
            if (chunk.world !== world) {
                continue;
            }
            this.createChunkRect(chunk.x * this.CHUNK_SIZE, chunk.z * this.CHUNK_SIZE, {
                color: chunk.color,
                fillOpacity: chunk.owned ? 0.6 : 0.45,
                opacity: 0.8,
                stroke: gridOn,
                modifier: chunk.owned ? "colored-owned" : "colored",
            }).addTo(this._coloredLayer);
        }

        this.redrawRegionBorders();
    }

    /** Highlight the chunks of the currently selected zone. */
    private redrawHighlightChunks(): void {
        if (!this._highlightLayer) {
            this._highlightLayer = L.layerGroup().addTo(this._pl3xmap.map);
        }
        this._highlightLayer.clearLayers();

        for (const chunk of this._highlightChunks) {
            this.createChunkRect(chunk.x * this.CHUNK_SIZE, chunk.z * this.CHUNK_SIZE, {
                color: "#05d9e8",
                fillOpacity: 0.25,
                weightScale: 1.5,
                modifier: "highlight",
            }).addTo(this._highlightLayer);
        }
    }

    /**
     * Render the markup-view overlay: faint white for unassigned region chunks,
     * per-zone random colour for assigned ones. Replaces the normal coloured-chunk
     * layer while the user is in markup-assignment mode.
     */
    private redrawMarkupView(): void {
        if (!this._markupViewLayer) {
            this._markupViewLayer = L.layerGroup().addTo(this._pl3xmap.map);
        }
        this._markupViewLayer.clearLayers();

        if (!this._markupViewEnabled) {
            return;
        }

        const gridOn = this._isGridActive;

        for (const chunk of this._markupViewChunks) {
            const chunkX = chunk.x * this.CHUNK_SIZE;
            const chunkZ = chunk.z * this.CHUNK_SIZE;

            if (chunk.markupId !== null) {
                const color = this._markupViewColors[String(chunk.markupId)] ?? "#aaaaaa";
                this.createChunkRect(chunkX, chunkZ, {
                    color,
                    fillOpacity: 0.65,
                    opacity: 0.85,
                    stroke: gridOn,
                    modifier: "markup-zone",
                }).addTo(this._markupViewLayer);
            } else {
                // Unassigned: subtle white wash so the region outline is visible
                // without the distracting per-chunk hex colours.
                this.createChunkRect(chunkX, chunkZ, {
                    color: "#ffffff",
                    fillOpacity: 0.18,
                    stroke: false,
                    modifier: "markup-region",
                }).addTo(this._markupViewLayer);
            }
        }
    }

    /**
     * Draw region borders in pure view mode (grid off). Each boundary face is
     * rendered as an individual line segment coloured by its adjacent chunk and
     * lightened toward white, so the border visually "reads" the region's colour
     * pattern rather than a single flat colour. Every region gets an outline —
     * foreign ones exactly like our own, otherwise they are hard to spot.
     */
    private redrawRegionBorders(): void {
        if (!this._regionBorderLayer) {
            this._regionBorderLayer = L.layerGroup().addTo(this._pl3xmap.map);
        }
        this._regionBorderLayer.clearLayers();

        if (this._isGridActive || this._markupViewEnabled) {
            return;
        }

        const world = this.currentWorldName();

        // Build a per-chunk colour map grouped by regionId. regionId is unique
        // across the whole map, so own and foreign regions never merge here.
        const byRegion = new Map<number, Map<string, string>>();
        for (const chunk of this._coloredChunks) {
            if (chunk.world !== world || chunk.regionId == null) {
                continue;
            }
            let colorMap = byRegion.get(chunk.regionId);
            if (!colorMap) {
                colorMap = new Map();
                byRegion.set(chunk.regionId, colorMap);
            }
            colorMap.set(`${chunk.x},${chunk.z}`, chunk.color);
        }

        const weight = Math.max(1.2, Math.min(4, this.chunkScreenPx() * 0.06));

        for (const chunkColors of byRegion.values()) {
            for (const { from, to, color } of this.computeBoundaryEdgesColored(chunkColors)) {
                L.polyline([from, to], {
                    color: this.lightenHex(color, 0.45),
                    weight,
                    opacity: 0.85,
                    lineCap: "square",
                    lineJoin: "miter",
                    interactive: false,
                    className: "chunk-cell--region-border",
                }).addTo(this._regionBorderLayer);
            }
        }
    }

    /**
     * Returns every exterior face of the chunk set as a directed line segment
     * carrying the colour of the chunk that owns that face.
     */
    private computeBoundaryEdgesColored(
        chunkColors: Map<string, string>,
    ): Array<{ from: L.LatLng; to: L.LatLng; color: string }> {
        const S = this.CHUNK_SIZE;
        const chunkSet = new Set(chunkColors.keys());
        const edges: Array<{ from: L.LatLng; to: L.LatLng; color: string }> = [];

        for (const key of chunkSet) {
            const [cx, cz] = key.split(",").map(Number);
            const color = chunkColors.get(key)!;
            const latN = pixelsToMeters(cz * S);
            const latS = pixelsToMeters((cz + 1) * S);
            const lngW = pixelsToMeters(cx * S);
            const lngE = pixelsToMeters((cx + 1) * S);

            if (!chunkSet.has(`${cx},${cz - 1}`))
                edges.push({ from: L.latLng(latN, lngW), to: L.latLng(latN, lngE), color });
            if (!chunkSet.has(`${cx + 1},${cz}`))
                edges.push({ from: L.latLng(latN, lngE), to: L.latLng(latS, lngE), color });
            if (!chunkSet.has(`${cx},${cz + 1}`))
                edges.push({ from: L.latLng(latS, lngE), to: L.latLng(latS, lngW), color });
            if (!chunkSet.has(`${cx - 1},${cz}`))
                edges.push({ from: L.latLng(latS, lngW), to: L.latLng(latN, lngW), color });
        }

        return edges;
    }

    /** Highlight one marker (or none, with `null`) as the selected one. */
    public setSelectedMarker(markerId: number | null): void {
        if (this._selectedMarkerId === markerId) {
            return;
        }
        this._selectedMarkerId = markerId;
        this.redrawMapMarkers();
    }

    /** Show or hide every player-placed map marker (see MarkerToggleControl). */
    public setMarkersVisible(visible: boolean): void {
        if (this._markersVisible === visible) {
            return;
        }
        this._markersVisible = visible;
        this.redrawMapMarkers();
    }

    /**
     * Render player-placed map markers as fixed-size pins. Uses L.marker with a
     * divIcon so the SVG keeps a constant on-screen size at every zoom level.
     * Independent of grid/markup/panel state — only the markers toggle hides them.
     */
    private redrawMapMarkers(): void {
        if (!this._markerLayer) {
            this._markerLayer = L.layerGroup().addTo(this._pl3xmap.map);
        }
        this._markerLayer.clearLayers();

        if (!this._markersVisible) {
            return;
        }

        const world = this.currentWorldName();

        for (const marker of this._mapMarkers) {
            if (marker.world !== world) {
                continue;
            }

            const selected: boolean = marker.id === this._selectedMarkerId;
            // The selected pin is drawn enlarged by CSS; the tooltip has to clear
            // that bigger silhouette.
            const scale: number = selected ? this.selectedPinScale() : 1;

            const icon = L.divIcon({
                html: this.markerPinHtml(marker.type),
                // Colour and size live in CSS (the pin SVG paints with
                // currentColor), so the selected pin only needs its modifier class.
                className: `map-marker-pin${selected ? " map-marker-pin--selected" : ""}`,
                iconSize: [30, 36],
                iconAnchor: [15, 34], // tip of the pin sits on the coordinate
                tooltipAnchor: [0, -36 * scale],
            });

            L.marker([pixelsToMeters(marker.z), pixelsToMeters(marker.x)], {
                icon,
                interactive: true,
                riseOnHover: true,
                // A pin click opens the marker's card and nothing else — without
                // this the same click would also reach the map and toggle a chunk.
                bubblingMouseEvents: false,
            })
                .bindTooltip(marker.name, { direction: "top", opacity: 0.95 })
                .on("click", (): void => {
                    // Select right away so the pin reacts to the click without
                    // waiting for the parent to echo the selection back.
                    this.setSelectedMarker(marker.id);
                    this.sendMarkerClickedToParent(marker.id);
                })
                .addTo(this._markerLayer);
        }
    }

    /**
     * Overlay covering the whole isPrivate chunk's footprint, for the current
     * world. Always rendered — not gated by grid/edit state — same as coloured
     * chunks and map markers, since privacy is a fact about the chunk, not an
     * editing concern.
     *
     * Bound to the chunk's own LatLngBounds via L.svgOverlay instead of a
     * marker/divIcon: a divIcon keeps a fixed pixel size no matter how far you
     * zoom, so it drifted out of sync with the chunk's actual on-screen size.
     * An SVGOverlay's viewBox is stretched to its bounds, so the border/glyph
     * always match the chunk exactly, at every zoom level.
     */
    private redrawPrivateChunkMarkers(): void {
        if (!this._privateMarkerLayer) {
            this._privateMarkerLayer = L.layerGroup().addTo(this._pl3xmap.map);
        }
        this._privateMarkerLayer.clearLayers();
        this._privateChunkKeys.clear();

        const world = this.currentWorldName();

        for (const chunk of this._privateChunkMarkers) {
            if (chunk.world !== world) {
                continue;
            }

            this._privateChunkKeys.add(`${chunk.x},${chunk.z}`);

            const chunkX = chunk.x * this.CHUNK_SIZE;
            const chunkZ = chunk.z * this.CHUNK_SIZE;
            const bounds: L.LatLngBoundsLiteral = [
                [pixelsToMeters(chunkZ), pixelsToMeters(chunkX)],
                [pixelsToMeters(chunkZ + this.CHUNK_SIZE), pixelsToMeters(chunkX + this.CHUNK_SIZE)],
            ];

            L.svgOverlay(this.parseSvg(this.privateChunkOverlaySvg()), bounds, {
                interactive: false,
                className: "private-chunk-overlay",
            }).addTo(this._privateMarkerLayer);
        }
    }

    /**
     * Dark radial vignette (darkest at the centre, dissolving to nothing
     * toward the edges) + a coloured border + a safe-box glyph, all in one
     * SVG whose viewBox maps 1:1 onto the chunk's own bounds.
     */
    private privateChunkOverlaySvg(): string {
        return `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <defs>
                <radialGradient id="privateChunkVignette" cx="50%" cy="50%" r="70%">
                    <stop offset="0%" stop-color="#000000" stop-opacity="0.55"/>
                    <stop offset="100%" stop-color="#000000" stop-opacity="0"/>
                </radialGradient>
            </defs>
            <rect x="0" y="0" width="100" height="100" fill="url(#privateChunkVignette)"/>
            <rect x="1.5" y="1.5" width="97" height="97" fill="none" stroke="#ffee58" stroke-width="2.5"/>
            <g transform="translate(50 50) scale(1.6) translate(-12 -12)">
                <path d="M15 2H9v2H7v4H4v14h16V8h-3V4h-2V2zm0 2v4H9V4h6zm-6 6h9v10H6V10h3zm4 3h-2v4h2v-4z" fill="#ffee58"/>
            </g>
        </svg>`;
    }

    /** Parse raw `<svg>` markup into a live SVGElement — L.svgOverlay needs an actual element, not a markup string. */
    private parseSvg(markup: string): SVGElement {
        return new DOMParser().parseFromString(markup, "image/svg+xml").documentElement as unknown as SVGElement;
    }

    /**
     * How much bigger the selected pin is drawn. Read from the stylesheet so the
     * factor stays defined in exactly one place (--marker-pin-selected-scale).
     */
    private selectedPinScale(): number {
        const raw: string = getComputedStyle(document.documentElement).getPropertyValue("--marker-pin-selected-scale");
        return parseFloat(raw) || 1;
    }

    /**
     * Pin for one marker: the shared teardrop shape plus the parent-supplied
     * glyph for that marker type, laid over the pin head. Types the parent
     * hasn't sent an icon for fall back to a plain dot.
     */
    private markerPinHtml(type: string): string {
        // The wrapper is what CSS scales (hover / selected) — leaflet owns the
        // transform on the icon element itself.
        return `<span class="map-marker-pin__inner">
                <span class="map-marker-pin__shape">${this.markerShapeSvg()}</span>
                <span class="map-marker-pin__glyph">${this._markerIcons[type] ?? this.defaultGlyphSvg()}</span>
            </span>`;
    }

    /**
     * Square badge with a pointed tail, shared by every marker whatever its
     * type. Body and tail are one path so the outline never crosses itself.
     * Painted with `currentColor` so the selected-pin colour is a CSS concern.
     */
    private markerShapeSvg(): string {
        return `<svg viewBox="0 0 30 36" width="30" height="36" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.5 1.5H25.5A3 3 0 0 1 28.5 4.5V24.5A3 3 0 0 1 25.5 27.5H18.5L15 34L11.5 27.5H4.5A3 3 0 0 1 1.5 24.5V4.5A3 3 0 0 1 4.5 1.5Z"
                  fill="currentColor" stroke="#0b1a20" stroke-width="1.5" stroke-linejoin="round"/>
            <rect x="3.5" y="3.5" width="23" height="22" rx="2" fill="#0b1a20"/>
        </svg>`;
    }

    /** Glyph used until (or unless) the parent supplies one for the type. */
    private defaultGlyphSvg(): string {
        return `<svg viewBox="0 0 24 24" width="1em" height="1em" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="5" fill="currentColor"/>
        </svg>`;
    }

    /** Blend a hex colour toward white by `factor` (0 = original, 1 = white). */
    private lightenHex(hex: string, factor: number): string {
        const clamp = (n: number): number => Math.min(255, Math.max(0, n));
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        const lr = clamp(Math.round(r + (255 - r) * factor));
        const lg = clamp(Math.round(g + (255 - g) * factor));
        const lb = clamp(Math.round(b + (255 - b) * factor));
        const hex2 = (n: number): string => (n < 16 ? "0" : "") + n.toString(16);
        return `#${hex2(lr)}${hex2(lg)}${hex2(lb)}`;
    }

    /** Replace the selection cart with the given chunks (from the parent). */
    private applySelection(chunks: ChunkCoordinates[]): void {
        this._selectedChunks.clear();
        this._selectedRectangles.clear();
        if (this._selectionLayer) {
            this._selectionLayer.clearLayers();
        }
        for (const chunk of chunks) {
            const chunkKey = `${chunk.x},${chunk.z}`;
            this._selectedChunks.add(chunkKey);
            this.drawSelectedChunk(chunk.x * this.CHUNK_SIZE, chunk.z * this.CHUNK_SIZE, chunkKey);
        }
        this.notifySelectionChange();
    }

    /** Notify the parent that an existing map marker was clicked. */
    private sendMarkerClickedToParent(markerId: number): void {
        const message = { type: "pl3xmap:marker-clicked", markerId };
        if (window.parent !== window) {
            window.parent.postMessage(message, "*");
        }
        window.dispatchEvent(new CustomEvent("pl3xmap:marker-clicked", { detail: { markerId } }));
    }

    /** Notify the parent that a single (paintable) chunk was clicked. */
    private sendChunkClickedToParent(x: number, z: number): void {
        const message = { type: "pl3xmap:chunk-clicked", chunk: { x, z } };
        if (window.parent !== window) {
            window.parent.postMessage(message, "*");
        }
        window.dispatchEvent(new CustomEvent("pl3xmap:chunk-clicked", { detail: message.chunk }));
    }

    /**
     * Bind an always-on hover that shows zone tooltips even when the selection
     * grid is off (so opening the map immediately surfaces markup info).
     */
    private ensurePassiveHover(): void {
        if (this._passiveBound) {
            return;
        }
        this._passiveBound = true;
        const map = this._pl3xmap.map;
        map.on("mousemove", this.onPassiveMove);
        map.on("mouseout", this.onPassiveOut);
        // Keep zoom-scaled chunk borders consistent across zoom levels.
        map.on("zoomend", this.onZoomRestyle);
    }

    private onPassiveMove = (e: L.LeafletMouseEvent): void => {
        // The grid's own hover owns tooltips while active.
        if (this._isGridActive) {
            return;
        }

        const chunkX = Math.floor(e.latlng.lng / pixelsToMeters(this.CHUNK_SIZE)) * this.CHUNK_SIZE;
        const chunkZ = Math.floor(e.latlng.lat / pixelsToMeters(this.CHUNK_SIZE)) * this.CHUNK_SIZE;
        const chunkKey = this.getChunkKey(chunkX, chunkZ);

        if (this._passiveHighlight) {
            this._passiveHighlight.remove();
            this._passiveHighlight = undefined;
        }

        if (!this._markupByKey.has(chunkKey)) {
            return;
        }

        const worldChunkX = Math.floor(chunkX / this.CHUNK_SIZE);
        const worldChunkZ = Math.floor(chunkZ / this.CHUNK_SIZE);

        this._passiveHighlight = this.createChunkRect(chunkX, chunkZ, {
            color: "#05d9e8",
            fillOpacity: 0.15,
            weightScale: 1.5,
            modifier: "hover",
        }).addTo(this._pl3xmap.map);

        this._passiveHighlight
            .bindTooltip(`Chunk: ${worldChunkX}, ${worldChunkZ}${this.markupTooltipHtml(chunkKey)}`, {
                permanent: false,
                direction: "top",
            })
            .openTooltip();
    };

    private onPassiveOut = (): void => {
        if (this._passiveHighlight) {
            this._passiveHighlight.remove();
            this._passiveHighlight = undefined;
        }
    };
}
